import { api } from './api';
import {
  UserListQueryParams,
  UserListResponse,
  UserDetailResponse,
  UserBookingsQueryParams,
  UserBookingsResponse,
  UserContentQueryParams,
  UserContentResponse,
  BookingDetailResponse,
  RegenerateVideoResponse,
} from '../types/user';
import {
  AvailabilityResponse,
  IdentityVerificationResponse,
  BackgroundVerificationResponse,
  AddressesResponse,
  ReportsQueryParams,
  ReportsResponse,
  FollowListResponse,
  FavoritesResponse,
  BlockedUsersResponse,
  ConnectedAccountsResponse,
  BadgesResponse,
  NotificationsQueryParams,
  UserNotificationsResponse,
  ReferralsResponse,
  XuBuxResponse,
  HouseholdResponse,
  ChatSummaryResponse,
  CallsQueryParams,
  CallsResponse,
  DevicesResponse,
  AuthSessionsResponse,
  RecordingsResponse,
  ContentEngagementResponse,
  LocationResponse,
  PromoRedemptionsResponse,
  IntroVideoResponse,
  ContactListsResponse,
  UserServiceRequestsResponse,
  GroupSessionsQueryParams,
  GroupSessionsResponse,
  UserEducation,
  UserCertification,
  UserWorkExperience,
  ReviewsResponse,
  ReviewsQueryParams,
  PaymentsResponse,
  PaymentsQueryParams,
} from '../types/userTabs';

const BASE_PATH = '/admin/users';

// ============================================================================
// Users API Service
// ============================================================================

/**
 * Clean undefined/empty values from params for axios.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const cleanQueryParams = (params: any): Record<string, string | number> => {
  const cleanParams: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') {
      cleanParams[key] = value as string | number;
    }
  }
  return cleanParams;
};

/**
 * List users with search, filters, and pagination.
 * GET /admin/users
 */
export const getUserList = async (
  params: UserListQueryParams = {}
): Promise<UserListResponse> => {
  const response = await api.get(BASE_PATH, { params: cleanQueryParams(params) });
  return response.data;
};

/**
 * Get detailed user profile with aggregated metrics.
 * GET /admin/users/:id
 */
export const getUserDetail = async (
  id: number
): Promise<UserDetailResponse> => {
  const response = await api.get(`${BASE_PATH}/${id}`);
  return response.data;
};

/**
 * Get user's booking history with filters and pagination.
 * GET /admin/users/:id/bookings
 */
export const getUserBookings = async (
  id: number,
  params: UserBookingsQueryParams = {}
): Promise<UserBookingsResponse> => {
  const response = await api.get(`${BASE_PATH}/${id}/bookings`, {
    params: cleanQueryParams(params),
  });
  return response.data;
};

/**
 * Get user's media content with filters and pagination.
 * GET /admin/users/:id/content
 */
export const getUserContent = async (
  id: number,
  params: UserContentQueryParams = {}
): Promise<UserContentResponse> => {
  const response = await api.get(`${BASE_PATH}/${id}/content`, {
    params: cleanQueryParams(params),
  });
  return response.data;
};

/**
 * Get full booking detail with all relations.
 * GET /admin/bookings/:id
 */
export const getBookingDetail = async (
  id: number
): Promise<BookingDetailResponse> => {
  const response = await api.get(`/admin/bookings/${id}`);
  return response.data;
};

/**
 * Regenerate an AI-generated service video.
 * POST /admin/content/:id/regenerate
 */
export const regenerateVideo = async (
  assetId: string
): Promise<RegenerateVideoResponse> => {
  const response = await api.post(`/admin/content/${assetId}/regenerate`);
  return response.data;
};

// ============================================================================
// User Tab Endpoints
// ============================================================================

export const getUserAvailability = async (id: number): Promise<AvailabilityResponse> => {
  const response = await api.get(`${BASE_PATH}/${id}/availability`);
  return response.data;
};

export const getUserIdentityVerification = async (id: number): Promise<IdentityVerificationResponse> => {
  const response = await api.get(`${BASE_PATH}/${id}/identity-verification`);
  return response.data;
};

export const getUserBackgroundVerification = async (id: number): Promise<BackgroundVerificationResponse> => {
  const response = await api.get(`${BASE_PATH}/${id}/background-verification`);
  return response.data;
};

export const getUserAddresses = async (id: number): Promise<AddressesResponse> => {
  const response = await api.get(`${BASE_PATH}/${id}/addresses`);
  return response.data;
};

export const getUserEducation = async (id: number): Promise<{ success: boolean; data: UserEducation[] }> => {
  const response = await api.get(`${BASE_PATH}/${id}/education`);
  return response.data;
};

export const getUserCertifications = async (id: number): Promise<{ success: boolean; data: UserCertification[] }> => {
  const response = await api.get(`${BASE_PATH}/${id}/certifications`);
  return response.data;
};

export const getUserWorkExperience = async (id: number): Promise<{ success: boolean; data: UserWorkExperience[] }> => {
  const response = await api.get(`${BASE_PATH}/${id}/work-experience`);
  return response.data;
};

export const getUserReports = async (
  id: number,
  params: ReportsQueryParams = {}
): Promise<ReportsResponse> => {
  const response = await api.get(`${BASE_PATH}/${id}/reports`, { params: cleanQueryParams(params) });
  return response.data;
};

export const getUserFollowers = async (id: number, limit = 50, offset = 0): Promise<FollowListResponse> => {
  const response = await api.get(`${BASE_PATH}/${id}/followers`, { params: { limit, offset } });
  return response.data;
};

export const getUserFollowing = async (id: number, limit = 50, offset = 0): Promise<FollowListResponse> => {
  const response = await api.get(`${BASE_PATH}/${id}/following`, { params: { limit, offset } });
  return response.data;
};

export const getUserFavorites = async (id: number): Promise<FavoritesResponse> => {
  const response = await api.get(`${BASE_PATH}/${id}/favorites`);
  return response.data;
};

export const getUserBlockedUsers = async (id: number): Promise<BlockedUsersResponse> => {
  const response = await api.get(`${BASE_PATH}/${id}/blocked-users`);
  return response.data;
};

export const getUserConnectedAccounts = async (id: number): Promise<ConnectedAccountsResponse> => {
  const response = await api.get(`${BASE_PATH}/${id}/connected-accounts`);
  return response.data;
};

export const getUserBadges = async (id: number): Promise<BadgesResponse> => {
  const response = await api.get(`${BASE_PATH}/${id}/badges`);
  return response.data;
};

export const getUserNotifications = async (
  id: number,
  params: NotificationsQueryParams = {}
): Promise<UserNotificationsResponse> => {
  const response = await api.get(`${BASE_PATH}/${id}/notifications`, { params: cleanQueryParams(params) });
  return response.data;
};

export const getUserReferrals = async (id: number): Promise<ReferralsResponse> => {
  const response = await api.get(`${BASE_PATH}/${id}/referrals`);
  return response.data;
};

export const getUserXuBux = async (id: number): Promise<XuBuxResponse> => {
  const response = await api.get(`${BASE_PATH}/${id}/xubux`);
  return response.data;
};

export const getUserHousehold = async (id: number): Promise<HouseholdResponse> => {
  const response = await api.get(`${BASE_PATH}/${id}/household`);
  return response.data;
};

export const getUserChatSummary = async (id: number): Promise<ChatSummaryResponse> => {
  const response = await api.get(`${BASE_PATH}/${id}/chat-summary`);
  return response.data;
};

export const getUserCalls = async (
  id: number,
  params: CallsQueryParams = {}
): Promise<CallsResponse> => {
  const response = await api.get(`${BASE_PATH}/${id}/calls`, { params: cleanQueryParams(params) });
  return response.data;
};

export const getUserDevices = async (id: number): Promise<DevicesResponse> => {
  const response = await api.get(`${BASE_PATH}/${id}/devices`);
  return response.data;
};

export const getUserAuthSessions = async (id: number): Promise<AuthSessionsResponse> => {
  const response = await api.get(`${BASE_PATH}/${id}/auth-sessions`);
  return response.data;
};

export const getUserRecordings = async (id: number): Promise<RecordingsResponse> => {
  const response = await api.get(`${BASE_PATH}/${id}/session-recordings`);
  return response.data;
};

export const getUserContentEngagement = async (id: number): Promise<ContentEngagementResponse> => {
  const response = await api.get(`${BASE_PATH}/${id}/content-engagement`);
  return response.data;
};

export const getUserLocation = async (id: number): Promise<LocationResponse> => {
  const response = await api.get(`${BASE_PATH}/${id}/location-details`);
  return response.data;
};

export const getUserPromoRedemptions = async (id: number): Promise<PromoRedemptionsResponse> => {
  const response = await api.get(`${BASE_PATH}/${id}/promo-redemptions`);
  return response.data;
};

export const getUserIntroVideo = async (id: number): Promise<IntroVideoResponse> => {
  const response = await api.get(`${BASE_PATH}/${id}/intro-video`);
  return response.data;
};

export const getUserContactLists = async (id: number): Promise<ContactListsResponse> => {
  const response = await api.get(`${BASE_PATH}/${id}/contact-lists`);
  return response.data;
};

export const getUserServiceRequests = async (id: number): Promise<UserServiceRequestsResponse> => {
  const response = await api.get(`${BASE_PATH}/${id}/service-requests`);
  return response.data;
};

export const getUserReviews = async (
  id: number,
  params: ReviewsQueryParams = {}
): Promise<ReviewsResponse> => {
  const response = await api.get(`${BASE_PATH}/${id}/reviews`, { params: cleanQueryParams(params) });
  return response.data;
};

export const getUserGroupSessions = async (
  id: number,
  params: GroupSessionsQueryParams = {}
): Promise<GroupSessionsResponse> => {
  const response = await api.get(`${BASE_PATH}/${id}/group-sessions`, { params: cleanQueryParams(params) });
  return response.data;
};

export const getUserPayments = async (
  id: number,
  params: PaymentsQueryParams = {}
): Promise<PaymentsResponse> => {
  const response = await api.get(`${BASE_PATH}/${id}/payments`, { params: cleanQueryParams(params) });
  return response.data;
};

// ============================================================================
// Export as usersService
// ============================================================================

export const usersService = {
  getUserList,
  getUserDetail,
  getUserBookings,
  getUserContent,
  getBookingDetail,
  regenerateVideo,
  getUserAvailability,
  getUserIdentityVerification,
  getUserBackgroundVerification,
  getUserAddresses,
  getUserEducation,
  getUserCertifications,
  getUserWorkExperience,
  getUserReports,
  getUserFollowers,
  getUserFollowing,
  getUserFavorites,
  getUserBlockedUsers,
  getUserConnectedAccounts,
  getUserBadges,
  getUserNotifications,
  getUserReferrals,
  getUserXuBux,
  getUserHousehold,
  getUserChatSummary,
  getUserCalls,
  getUserDevices,
  getUserAuthSessions,
  getUserRecordings,
  getUserContentEngagement,
  getUserLocation,
  getUserPromoRedemptions,
  getUserIntroVideo,
  getUserContactLists,
  getUserServiceRequests,
  getUserReviews,
  getUserGroupSessions,
  getUserPayments,
};

export default usersService;
